const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

export type VideoEntry = {
  filename: string;
  display_name?: string;
  size_bytes: number;
  updated_at: string;
  url: string;
  thumbnail?: string;
};

export type ModelOption = { key: string; display: string; provider: string; max_tokens?: number | null };
export type ApiConfig = {
  upload_dir: string;
  gallery_dir: string | null;
  cors_origins: string[];
  enable_sse: boolean;
  max_workers: number;
  models: ModelOption[];
};

export type CreateJobPayload = {
  topic?: string;
  pdf_id?: string;
  model_key?: string | null;
  code_model_key?: string | null;
  use_feedback?: boolean;
  use_assets?: boolean;
  funny_mode?: boolean;
  save_pdf_index?: boolean;
  max_pages_for_context?: number;
  theme?: string;
  orientation?: string;
  layout_mode?: string;
};

export type JobResponse = { job_id: string; status: string };

export type JobDetail = {
  job_id: string;
  status: "queued" | "running" | "completed" | "failed" | "cancelled";
  topic?: string | null;
  pdf_id?: string | null;
  progress?: number | null;
  progress_stage?: string | null;
  progress_message?: string | null;
  gallery_path?: string | null;
  video_url?: string | null;
  thumbnail_url?: string | null;
  error?: string | null;
  logs: string[];
};

export async function fetchConfig(): Promise<ApiConfig> {
  const res = await fetch(`${API_BASE}/config`);
  if (!res.ok) throw new Error(`Failed to load config (${res.status})`);
  return res.json();
}

export async function fetchGallery(): Promise<VideoEntry[]> {
  const response = await fetch(`${API_BASE}/videos`, { headers: { Accept: "application/json" } });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Failed to load videos: ${response.status}`);
  }
  return response.json();
}

export function getVideoStreamUrl(filename: string): string {
  // Convenience helper; backend streams/redirects from /videos/{filename}
  return `${API_BASE}/videos/${encodeURIComponent(filename)}`;
}

export function resolveVideoUrl(url: string): string {
  // If the URL is already absolute, return as-is
  if (/^(?:https?:)?\/\//i.test(url)) {
    return url;
  }
  // If the URL is relative to the API (e.g., /api/videos/... or /videos/...)
  // construct the full URL using API_BASE
  if (url.startsWith("/api/")) {
    // Strip /api prefix since API_BASE already includes it
    return `${API_BASE}${url.slice(4)}`;
  }
  if (url.startsWith("/videos/")) {
    return `${API_BASE}${url}`;
  }
  // Fallback: return as-is (might be a local asset path)
  return url;
}

export async function fetchReadme(): Promise<string> {
  const response = await fetch(`${import.meta.env.BASE_URL}README.md`);
  if (!response.ok) {
    throw new Error(`Failed to load README: ${response.status}`);
  }
  return response.text();
}

export async function uploadPdf(file: File): Promise<{ pdf_id: string; filename: string; size_bytes: number }> {
  const form = new FormData();
  form.append("upload", file);
  const res = await fetch(`${API_BASE}/files/pdf`, { method: "POST", body: form });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createJob(payload: CreateJobPayload): Promise<JobResponse> {
  const res = await fetch(`${API_BASE}/jobs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getJob(jobId: string): Promise<JobDetail> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export type RetryJobPayload = {
  mode: "restart" | "resume";
};

export type RetryJobResponse = {
  job_id: string;
  status: string;
  last_completed_stage?: string | null;
  resume_from_stage?: string | null;
};

export async function retryJob(jobId: string, payload: RetryJobPayload): Promise<RetryJobResponse> {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/retry`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ============================================================================
// Studio API
// ============================================================================

export type SectionArtifact = {
  section_id: string;
  title: string;
  code_path?: string | null;
  code_filename?: string | null;
  video_path?: string | null;
  video_filename?: string | null;
  status: "pending" | "code_only" | "rendered" | "failed";
  has_error_log: boolean;
  error_log_path?: string | null;
};

export type StudioJobSummary = {
  directory: string;
  provider: string;
  topic_slug: string;
  topic: string;
  has_outline: boolean;
  has_storyboard: boolean;
  section_count: number;
  final_video?: string | null;
  final_video_name?: string | null;
  modified_at: string;
};

export type StudioJobArtifacts = {
  directory: string;
  topic_slug: string;
  provider?: string | null;
  outline?: Record<string, unknown> | null;
  outline_error?: string | null;
  storyboard?: Record<string, unknown> | null;
  storyboard_error?: string | null;
  storyboard_with_assets?: Record<string, unknown> | null;
  sections: SectionArtifact[];
  final_video?: string | null;
  final_video_name?: string | null;
  voiceover_files: string[];
};

export type StudioJobsParams = {
  search?: string;
  provider?: string;
  has_video?: boolean;
  limit?: number;
  offset?: number;
};

export type StudioStats = {
  total_jobs: number;
  jobs_with_video: number;
  jobs_without_video: number;
  providers: Record<string, number>;
  total_sections: number;
};

// Experiments
export type Experiment = {
  id: string;
  name: string;
  source_job_id?: string | null;
  created_at?: string | null;
};

export type ExperimentVariant = {
  id: string;
  experiment_id: string;
  job_id?: string | null;
  changes?: Record<string, unknown> | null;
  is_favorite: boolean;
  created_at?: string | null;
};

export type ExperimentCompare = {
  experiment: Experiment;
  variants: ExperimentVariant[];
};

export async function listStudioJobs(params?: StudioJobsParams): Promise<StudioJobSummary[]> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set("search", params.search);
  if (params?.provider) searchParams.set("provider", params.provider);
  if (params?.has_video !== undefined) searchParams.set("has_video", String(params.has_video));
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.offset) searchParams.set("offset", String(params.offset));

  const query = searchParams.toString();
  const url = `${API_BASE}/studio/jobs${query ? `?${query}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getStudioStats(): Promise<StudioStats> {
  const res = await fetch(`${API_BASE}/studio/jobs/stats`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getStudioProviders(): Promise<string[]> {
  const res = await fetch(`${API_BASE}/studio/providers`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getJobArtifacts(directory: string): Promise<StudioJobArtifacts> {
  const res = await fetch(`${API_BASE}/studio/artifacts?directory=${encodeURIComponent(directory)}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Experiments API
export async function createExperiment(name: string, sourceJobId?: string): Promise<Experiment> {
  const res = await fetch(`${API_BASE}/studio/experiments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, source_job_id: sourceJobId }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listExperiments(limit = 100): Promise<Experiment[]> {
  const res = await fetch(`${API_BASE}/studio/experiments?limit=${limit}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createVariant(
  experimentId: string,
  options?: {
    jobId?: string;
    changes?: Record<string, unknown>;
    isFavorite?: boolean;
    sourceDirectory?: string;
    copyArtifacts?: boolean;
    promptOverride?: string;
    modelKey?: string;
    notes?: string;
    runJob?: boolean;
  }
): Promise<ExperimentVariant> {
  const res = await fetch(`${API_BASE}/studio/experiments/${experimentId}/variants`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      experiment_id: experimentId,
      job_id: options?.jobId,
      changes: options?.changes,
      is_favorite: options?.isFavorite ?? false,
      source_directory: options?.sourceDirectory,
      copy_artifacts: options?.copyArtifacts ?? false,
      prompt_override: options?.promptOverride,
      model_key: options?.modelKey,
      notes: options?.notes,
      run_job: options?.runJob ?? false,
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listVariants(experimentId: string): Promise<ExperimentVariant[]> {
  const res = await fetch(`${API_BASE}/studio/experiments/${experimentId}/variants`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function compareExperiment(experimentId: string): Promise<ExperimentCompare> {
  const res = await fetch(`${API_BASE}/studio/experiments/${experimentId}/compare`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function setVariantFavorite(
  experimentId: string,
  variantId: string,
  isFavorite: boolean
): Promise<ExperimentVariant> {
  const res = await fetch(
    `${API_BASE}/studio/experiments/${experimentId}/variants/${variantId}/favorite?is_favorite=${isFavorite}`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getSectionCode(directory: string, sectionId: string): Promise<string> {
  const res = await fetch(
    `${API_BASE}/studio/artifacts/sections/${sectionId}/code?directory=${encodeURIComponent(directory)}`
  );
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.code;
}

export async function getAllSectionsCode(
  directory: string,
  sectionIds: string[],
  titles?: string[]
): Promise<string> {
  const codePromises = sectionIds.map((id) => getSectionCode(directory, id));
  const codes = await Promise.all(codePromises);

  // Combine all code with section headers
  return codes
    .map((code, index) => {
      const sectionId = sectionIds[index];
      const title = titles?.[index] || `Section ${sectionId}`;
      return `# ============================================\n# ${title} (${sectionId})\n# ============================================\n\n${code}`;
    })
    .join("\n\n");
}

export async function getSectionErrorLog(directory: string, sectionId: string): Promise<string | null> {
  const res = await fetch(
    `${API_BASE}/studio/artifacts/sections/${sectionId}/error-log?directory=${encodeURIComponent(directory)}`
  );
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.error_log;
}

export function getSectionVideoUrl(directory: string, sectionId: string): string {
  return `${API_BASE}/studio/artifacts/sections/${sectionId}/video?directory=${encodeURIComponent(directory)}`;
}

export function getFinalVideoUrl(directory: string): string {
  return `${API_BASE}/studio/artifacts/final-video?directory=${encodeURIComponent(directory)}`;
}

export async function getOutline(directory: string): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_BASE}/studio/artifacts/outline?directory=${encodeURIComponent(directory)}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getStoryboard(directory: string, withAssets = false): Promise<Record<string, unknown>> {
  const url = `${API_BASE}/studio/artifacts/storyboard?directory=${encodeURIComponent(directory)}&with_assets=${withAssets}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ============================================================================
// Section Operations
// ============================================================================

export type SaveSectionResult = {
  success: boolean;
  path?: string;
  backup_path?: string;
  saved_at?: string;
  error?: string;
};

export type RenderSectionResult = {
  success: boolean;
  section_id: string;
  command?: string;
  started_at?: string;
  completed_at?: string;
  returncode?: number;
  stdout?: string;
  stderr?: string;
  video_path?: string;
  video_filename?: string;
  error?: string;
  error_log_path?: string;
};

export type SaveAndRenderResult = {
  success: boolean;
  save: SaveSectionResult;
  render: RenderSectionResult;
};

export type SectionBackup = {
  path: string;
  filename: string;
  timestamp: string;
  size_bytes: number;
};

export type ValidateCodeResult = {
  section_id: string;
  valid: boolean;
  error?: string | null;
};

export type RegenerateSectionResult = {
  success: boolean;
  code?: string;
  path?: string;
  backup_path?: string;
  model_key?: string;
  section_id?: string;
  error?: string;
};

export async function saveSectionCode(
  directory: string,
  sectionId: string,
  code: string,
  createBackup = true
): Promise<SaveSectionResult> {
  const res = await fetch(`${API_BASE}/studio/sections/${sectionId}/save`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ directory, code, create_backup: createBackup }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function renderSection(
  directory: string,
  sectionId: string,
  options?: {
    timeout?: number;
    theme?: string;
    orientation?: string;
    layout_mode?: string;
  }
): Promise<RenderSectionResult> {
  const res = await fetch(`${API_BASE}/studio/sections/${sectionId}/render`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      directory,
      timeout: options?.timeout ?? 360,
      theme: options?.theme ?? "dark",
      orientation: options?.orientation ?? "landscape",
      layout_mode: options?.layout_mode ?? "classic",
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function saveAndRenderSection(
  directory: string,
  sectionId: string,
  code: string,
  options?: {
    timeout?: number;
    theme?: string;
    orientation?: string;
    layout_mode?: string;
  }
): Promise<SaveAndRenderResult> {
  const res = await fetch(`${API_BASE}/studio/sections/${sectionId}/save-and-render`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      directory,
      code,
      timeout: options?.timeout ?? 360,
      theme: options?.theme ?? "dark",
      orientation: options?.orientation ?? "landscape",
      layout_mode: options?.layout_mode ?? "classic",
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getSectionBackups(directory: string, sectionId: string): Promise<SectionBackup[]> {
  const res = await fetch(
    `${API_BASE}/studio/sections/${sectionId}/backups?directory=${encodeURIComponent(directory)}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function restoreSectionBackup(
  directory: string,
  sectionId: string,
  backupFilename: string
): Promise<{ success: boolean; restored_from?: string; restored_to?: string; restored_at?: string; error?: string }> {
  const res = await fetch(`${API_BASE}/studio/sections/${sectionId}/restore`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ directory, backup_filename: backupFilename }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function validateSectionCode(sectionId: string, code: string): Promise<ValidateCodeResult> {
  const res = await fetch(`${API_BASE}/studio/sections/${sectionId}/validate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function regenerateSection(
  directory: string,
  sectionId: string,
  promptOverride?: string,
  modelKey?: string
): Promise<RegenerateSectionResult> {
  const res = await fetch(`${API_BASE}/studio/sections/${sectionId}/regenerate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      directory,
      prompt_override: promptOverride,
      model_key: modelKey,
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ============================================================================
// SSE Events
// ============================================================================

export type JobEventType =
  | "log"
  | "progress"
  | "status"
  | "md_status"
  | "md_ready"
  | "video_ready"
  | "section_start"
  | "section_progress"
  | "section_complete"
  | "section_error";

export type JobEvent = {
  timestamp: string;
  type: JobEventType;
  message: string;
  stage?: string | null;
  level: string;
  // Section-level fields
  section_id?: string | null;
  section_title?: string | null;
  section_status?: string | null;
  total_sections?: number | null;
  completed_sections?: number | null;
};

export type SSECallbacks = {
  onEvent?: (event: JobEvent) => void;
  onLog?: (message: string, level: string) => void;
  onProgress?: (stage: string, message: string) => void;
  onStatus?: (status: string, message: string) => void;
  onSectionStart?: (sectionId: string, title: string, status: string) => void;
  onSectionProgress?: (sectionId: string, status: string, message: string) => void;
  onSectionComplete?: (sectionId: string, title: string, completed: number, total: number) => void;
  onSectionError?: (sectionId: string, error: string) => void;
  onVideoReady?: (path: string) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
};

export function subscribeToJobEvents(jobId: string, callbacks: SSECallbacks): () => void {
  const url = `${API_BASE}/jobs/${jobId}/events`;
  const eventSource = new EventSource(url);

  const handleEvent = (eventType: string, data: string) => {
    try {
      const event: JobEvent = JSON.parse(data);
      callbacks.onEvent?.(event);

      switch (eventType) {
        case "log":
          callbacks.onLog?.(event.message, event.level);
          break;
        case "progress":
          callbacks.onProgress?.(event.stage || "", event.message);
          break;
        case "status":
          callbacks.onStatus?.(event.message, event.message);
          break;
        case "section_start":
          callbacks.onSectionStart?.(
            event.section_id || "",
            event.section_title || "",
            event.section_status || ""
          );
          break;
        case "section_progress":
          callbacks.onSectionProgress?.(
            event.section_id || "",
            event.section_status || "",
            event.message
          );
          break;
        case "section_complete":
          callbacks.onSectionComplete?.(
            event.section_id || "",
            event.section_title || "",
            event.completed_sections || 0,
            event.total_sections || 0
          );
          break;
        case "section_error":
          callbacks.onSectionError?.(event.section_id || "", event.message);
          break;
        case "video_ready":
          callbacks.onVideoReady?.(event.message);
          break;
      }
    } catch (e) {
      console.error("Failed to parse SSE event:", e);
    }
  };

  // Listen for specific event types
  const eventTypes: JobEventType[] = [
    "log",
    "progress",
    "status",
    "md_status",
    "md_ready",
    "video_ready",
    "section_start",
    "section_progress",
    "section_complete",
    "section_error",
  ];

  eventTypes.forEach((type) => {
    eventSource.addEventListener(type, (e: MessageEvent) => handleEvent(type, e.data));
  });

  eventSource.onerror = (e) => {
    callbacks.onError?.(new Error("SSE connection error"));
    eventSource.close();
    callbacks.onClose?.();
  };

  // Return cleanup function
  return () => {
    eventSource.close();
    callbacks.onClose?.();
  };
}

// ============================================================================
// Analytics API
// ============================================================================

export type CostBreakdown = {
  label: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  estimated_cost_usd: number;
};

export type CostAnalytics = {
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  estimated_cost_usd: number;
  breakdown: CostBreakdown[];
};

export type StagePerformance = {
  stage: string;
  avg_duration_sec: number;
  min_duration_sec: number;
  max_duration_sec: number;
  p50_duration_sec: number;
  p95_duration_sec: number;
  sample_count: number;
};

export type PerformanceAnalytics = {
  avg_total_duration_sec: number;
  avg_stage_durations: Record<string, number>;
  p50_duration_sec: number;
  p95_duration_sec: number;
  stage_breakdown: StagePerformance[];
};

export type ErrorBreakdown = {
  stage: string;
  error_count: number;
  retry_count: number;
  avg_retries_before_success: number;
};

export type QualityAnalytics = {
  total_jobs: number;
  successful_jobs: number;
  failed_jobs: number;
  success_rate: number;
  avg_retry_count: number;
  error_breakdown: ErrorBreakdown[];
  cache_hit_rate: number;
};

export type StageMetric = {
  stage: string;
  section_id?: string | null;
  duration_sec: number;
  input_tokens?: number | null;
  output_tokens?: number | null;
  total_tokens?: number | null;
  model_key?: string | null;
  cache_hit: boolean;
  attempt_count: number;
  success: boolean;
  estimated_cost_usd: number;
};

export type JobAnalytics = {
  job_id: string;
  total_duration_sec: number;
  total_tokens: number;
  estimated_cost_usd: number;
  stages: StageMetric[];
};

export type AnalyticsParams = {
  startDate?: string;
  endDate?: string;
  modelKey?: string;
  stage?: string;
  groupBy?: string;
};

export async function getCostAnalytics(params?: AnalyticsParams): Promise<CostAnalytics> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set("start_date", params.startDate);
  if (params?.endDate) searchParams.set("end_date", params.endDate);
  if (params?.modelKey) searchParams.set("model_key", params.modelKey);
  if (params?.groupBy) searchParams.set("group_by", params.groupBy);

  const query = searchParams.toString();
  const url = `${API_BASE}/studio/analytics/costs${query ? `?${query}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getPerformanceAnalytics(params?: AnalyticsParams): Promise<PerformanceAnalytics> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set("start_date", params.startDate);
  if (params?.endDate) searchParams.set("end_date", params.endDate);
  if (params?.stage) searchParams.set("stage", params.stage);

  const query = searchParams.toString();
  const url = `${API_BASE}/studio/analytics/performance${query ? `?${query}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getQualityAnalytics(params?: AnalyticsParams): Promise<QualityAnalytics> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set("start_date", params.startDate);
  if (params?.endDate) searchParams.set("end_date", params.endDate);

  const query = searchParams.toString();
  const url = `${API_BASE}/studio/analytics/quality${query ? `?${query}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getJobAnalytics(jobId: string): Promise<JobAnalytics> {
  const res = await fetch(`${API_BASE}/studio/analytics/job/${jobId}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Layout Analytics Types

export type SceneLayoutBreakdown = {
  scene: string;
  occurrence_count: number;
  collisions: number;
  out_of_frame: number;
  timing_warnings: number;
  avg_score: number;
};

export type DailyLayoutBreakdown = {
  date: string;
  job_count: number;
  scene_count: number;
  collisions: number;
  out_of_frame: number;
  timing_warnings: number;
  avg_score: number;
};

export type LayoutAnalytics = {
  total_jobs: number;
  total_scenes: number;
  total_collisions: number;
  total_out_of_frame: number;
  total_timing_warnings: number;
  clean_scenes: number;
  clean_rate: number;
  avg_layout_score: number;
  scene_breakdown: SceneLayoutBreakdown[];
  daily_breakdown: DailyLayoutBreakdown[];
};

export type SceneLayoutDetail = {
  scene: string;
  module?: string | null;
  collision_count: number;
  out_of_frame_count: number;
  timing_warning_count: number;
  unit_count: number;
  layout_score: number;
  collision_events?: unknown[] | null;
  out_of_frame_events?: unknown[] | null;
  timing_warnings?: unknown[] | null;
};

export type JobLayoutAnalytics = {
  job_id: string;
  total_scenes: number;
  total_collisions: number;
  total_out_of_frame: number;
  total_timing_warnings: number;
  avg_layout_score: number;
  scenes: SceneLayoutDetail[];
};

export async function getLayoutAnalytics(params?: AnalyticsParams): Promise<LayoutAnalytics> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set("start_date", params.startDate);
  if (params?.endDate) searchParams.set("end_date", params.endDate);

  const query = searchParams.toString();
  const url = `${API_BASE}/studio/analytics/layout${query ? `?${query}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getJobLayoutAnalytics(jobId: string): Promise<JobLayoutAnalytics> {
  const res = await fetch(`${API_BASE}/studio/analytics/layout/job/${jobId}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
